/**
 * Voice WebSocket lifecycle. Connects to /api/voice-ws, sends Opus uplink
 * frames, parses Opus downlink frames into the audio engine. Auto-reconnect
 * with exponential backoff so a brief network blip recovers on its own.
 *
 * No snapshot WS in v1 - proximity is server-gated already, so the web
 * client doesn't need spatial gains to function. v2 will add the snapshot
 * subscription for L/R pan.
 */

import { refreshSession } from '../api.ts'
import { baseWsOrigin, getRefreshToken } from '../auth.ts'
import { AudioEngine } from './audio.ts'
import { decodeDownlink, encodeAudioUp, encodeHeartbeat } from './frames.ts'

const HEARTBEAT_INTERVAL_MS = 5_000
const RECONNECT_BASE_MS = 1_000
const RECONNECT_MAX_MS = 30_000

export interface VoiceConnectionOptions {
  readonly serverId: string
  /** Stable per-page client id so the bridge treats our own reconnects as a
   * replace (not a 4006 rejection). Shared with the RTC path. */
  readonly clientInstanceId: string
  readonly engine: AudioEngine
  /** Called whenever connection state changes (UI status). */
  readonly onState: (state: ConnectionState) => void
  /**
   * Called when the bridge rejects our session token irrecoverably (4002).
   * The caller should wipe the refresh token and bounce the user back to
   * the link page.
   */
  readonly onAuthFailed: () => void
}

export type ConnectionState =
  | { kind: 'connecting' }
  | { kind: 'connected'; sinceMs: number }
  | { kind: 'reconnecting'; attempt: number; nextDelayMs: number; reason: string }
  | { kind: 'stopped' }

export class VoiceConnection {
  private readonly opts: VoiceConnectionOptions
  private ws: WebSocket | null = null
  private heartbeatTimer: ReturnType<typeof setInterval> | null = null
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null
  private reconnectAttempt = 0
  private uplinkSeq = 0
  private stopped = false

  constructor(opts: VoiceConnectionOptions) {
    this.opts = opts
  }

  async start(): Promise<void> {
    this.stopped = false
    await this.connectOnce()
  }

  stop(): void {
    this.stopped = true
    if (this.heartbeatTimer !== null) {
      clearInterval(this.heartbeatTimer)
      this.heartbeatTimer = null
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    if (this.ws !== null) {
      try { this.ws.close(1000, 'client stopped') } catch { /* socket might be torn down */ }
      this.ws = null
    }
    this.opts.onState({ kind: 'stopped' })
  }

  /**
   * Force an immediate reconnect attempt, bypassing whatever exponential
   * backoff timer is pending. Used by the wake handler after the page is
   * brought back to the foreground: by then any pending backoff would
   * waste 30s waiting before the user gets audio back. Idempotent and
   * safe to call when already connected (becomes a no-op).
   */
  reconnectNow(): void {
    if (this.stopped) return
    if (this.ws !== null && this.ws.readyState === WebSocket.OPEN) return
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer)
      this.reconnectTimer = null
    }
    this.reconnectAttempt = 0
    void this.connectOnce()
  }

  sendOpus(opus: Uint8Array): void {
    const ws = this.ws
    if (ws === null || ws.readyState !== WebSocket.OPEN) return
    this.uplinkSeq = (this.uplinkSeq + 1) >>> 0
    ws.send(encodeAudioUp(this.uplinkSeq, opus))
  }

  private async connectOnce(): Promise<void> {
    this.opts.onState({ kind: 'connecting' })

    const refreshToken = getRefreshToken()
    if (refreshToken === null) {
      this.opts.onAuthFailed()
      return
    }

    let sessionToken: string
    try {
      const { sessionToken: t } = await refreshSession(refreshToken)
      sessionToken = t
    } catch (err) {
      // Most likely invalid/expired refresh token - punt to link page.
      // Network failure also lands here; we let the caller decide vs. retry.
      const msg = err instanceof Error ? err.message : String(err)
      if (msg.includes('invalid_token') || msg.includes('http_401')) {
        this.opts.onAuthFailed()
        return
      }
      this.scheduleReconnect(`refresh failed: ${msg}`)
      return
    }

    const url = `${baseWsOrigin()}/api/voice-ws?token=${encodeURIComponent(sessionToken)}&serverId=${encodeURIComponent(this.opts.serverId)}&client=${encodeURIComponent(this.opts.clientInstanceId)}`
    const ws = new WebSocket(url)
    ws.binaryType = 'arraybuffer'
    this.ws = ws

    ws.addEventListener('open', () => {
      this.reconnectAttempt = 0
      this.opts.onState({ kind: 'connected', sinceMs: Date.now() })
      // Heartbeats keep the bridge's idle sweep from dropping us; voice
      // frames also reset lastSeenAt server-side, but during PTT-off we
      // wouldn't send anything for minutes.
      this.heartbeatTimer = setInterval(() => {
        if (ws.readyState === WebSocket.OPEN) ws.send(encodeHeartbeat())
      }, HEARTBEAT_INTERVAL_MS)
    })

    ws.addEventListener('message', (event) => {
      if (!(event.data instanceof ArrayBuffer)) return
      const frame = decodeDownlink(event.data)
      if (frame === null) return
      if (frame.type === 'heartbeat') return
      if (frame.type === 'audioFwd') {
        this.opts.engine.ingestOpus(frame.senderSessionId, frame.agid, frame.opus)
      }
    })

    ws.addEventListener('close', (event) => {
      if (this.heartbeatTimer !== null) {
        clearInterval(this.heartbeatTimer)
        this.heartbeatTimer = null
      }
      this.ws = null
      if (this.stopped) return
      // 4002 = invalid token. 4404 = not linked to server. 4403 = banned.
      // None of these are retryable.
      if (event.code === 4002) {
        this.opts.onAuthFailed()
        return
      }
      // 4006 = the bridge already has an active voice session for our
      // identity (another tab / window / device). Tight auto-reconnect here
      // is exactly what created the kick-reconnect flap before the bridge
      // grew its grace check, and it would still waste bandwidth fighting
      // the incumbent. Back off long enough for the OTHER client to give
      // up cleanly if the user closed it; the user can also explicitly
      // reconnect via the UI.
      if (event.code === 4006) {
        this.scheduleReconnect(`already connected elsewhere (code=4006)`, 30_000)
        return
      }
      this.scheduleReconnect(`ws closed (code=${event.code} reason=${event.reason})`)
    })

    ws.addEventListener('error', () => {
      // The close handler will fire too; let it own the reconnect logic.
    })
  }

  private scheduleReconnect(reason: string, fixedDelayMs?: number): void {
    if (this.stopped) return
    this.reconnectAttempt += 1
    const delay = fixedDelayMs ?? Math.min(
      RECONNECT_BASE_MS * 2 ** Math.min(this.reconnectAttempt - 1, 5),
      RECONNECT_MAX_MS,
    )
    this.opts.onState({
      kind: 'reconnecting',
      attempt: this.reconnectAttempt,
      nextDelayMs: delay,
      reason,
    })
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null
      void this.connectOnce()
    }, delay)
  }
}
